import { useState, useRef, useEffect, useCallback } from 'react';
import { Logger } from '@/core/utils/logger';
const logger = new Logger('components');
import {
  optimizeImage,
  isFileSizeValid,
  generatePreviewUrl,
  revokePreviewUrl,
  CropArea
} from '../utils/imageOptimization';
import {
  IMAGE_SIZE_LIMITS,
  isValidImageType,
  getInvalidTypeMessage,
  getFileTooLargeMessage,
} from '@voley/shared-types/constants';
import { getErrorMessage } from '@voley/shared-utils';

/** Default retry configuration - exported for reuse */
export const UPLOAD_RETRY_CONFIG = {
  maxRetries: 2,
  baseDelayMs: 500,
  maxDelayMs: 3000,
} as const;

/**
 * Check if an error is likely a transient/network error that's safe to retry.
 * Exported for reuse in other upload components.
 */
export const isRetryableUploadError = (error: unknown): boolean => {
  const errObj = error as Record<string, unknown> | null | undefined;
  // RTK Query errors use `.error` for the message string, standard errors use `.message`
  const message = (
    errObj && typeof errObj.message === 'string' ? errObj.message
    : errObj && typeof errObj.error === 'string' ? errObj.error
    : ''
  ).toLowerCase();
  const errorData = errObj?.data;
  const errorDataMessage = (
    typeof errorData === 'object' && errorData !== null && 'message' in errorData
      ? String((errorData as Record<string, unknown>).message)
      : ''
  ).toLowerCase();

  // RTK Query FETCH_ERROR — network-level failure (request never reached server)
  const status = errObj?.status;
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return true;
  }

  // Network/timeout errors
  if (message.includes('timeout') || message.includes('network') || message.includes('fetch')) {
    return true;
  }

  // CORS errors that aren't real CORS errors (usually initialization issues)
  if (message.includes('cors') || message.includes('cross-origin')) {
    return true;
  }

  // Server temporarily unavailable
  if (errorDataMessage.includes('temporarily') || errorDataMessage.includes('unavailable')) {
    return true;
  }

  // Check for specific numeric status codes
  if (status === 502 || status === 503 || status === 504 || status === 0) {
    return true;
  }

  return false;
};

/**
 * Calculate delay for retry attempt with exponential backoff.
 * Exported for reuse in other upload components.
 */
export const getUploadRetryDelay = (attempt: number, baseDelayMs: number = UPLOAD_RETRY_CONFIG.baseDelayMs, maxDelayMs: number = UPLOAD_RETRY_CONFIG.maxDelayMs): number => {
  const delay = baseDelayMs * Math.pow(2, attempt);
  return Math.min(delay, maxDelayMs);
};

export interface UseImageUploadOptions {
  maxSizeMB?: number;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'webp' | 'png';
  onUpload?: (file: File, cropData?: CropArea) => Promise<void>;
  onError?: (error: string) => void;
  /** Maximum number of retry attempts for transient failures (default: 2) */
  maxRetries?: number;
}

export interface UseImageUploadReturn {
  // State
  isProcessing: boolean;
  uploadError: string | null;
  showCropper: boolean;
  tempImageUrl: string | null;

  // Methods
  handleImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleCropAndUpload: (cropArea: CropArea) => Promise<void>;
  handleCropperCancel: () => void;
  resetUpload: () => void;

  // Refs
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Custom hook for handling image uploads with optimization, cropping, and retry logic
 */
export const useImageUpload = (options: UseImageUploadOptions = {}): UseImageUploadReturn => {
  const {
    maxSizeMB = 10,
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.85,
    format = 'jpeg',
    onUpload,
    onError,
    maxRetries = UPLOAD_RETRY_CONFIG.maxRetries
  } = options;

  // State
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clean up temporary image URL on unmount
  useEffect(() => {
    return () => {
      if (tempImageUrl) {
        revokePreviewUrl(tempImageUrl);
      }
    };
  }, [tempImageUrl]);

  /**
   * Handle initial image selection
   */
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadError(null);
      setIsProcessing(true);

      // Check initial file size (use profile image limit which matches backend)
      const effectiveMaxMB = Math.min(maxSizeMB, IMAGE_SIZE_LIMITS.PROFILE_IMAGE_MAX_MB);
      if (!isFileSizeValid(file, effectiveMaxMB)) {
        const error = getFileTooLargeMessage(effectiveMaxMB);
        setUploadError(error);
        onError?.(error);
        setIsProcessing(false);
        return;
      }

      // Validate file type using shared constants
      if (!isValidImageType(file.type)) {
        const error = getInvalidTypeMessage();
        setUploadError(error);
        onError?.(error);
        setIsProcessing(false);
        return;
      }

      // Generate preview URL and show cropper
      const previewUrl = generatePreviewUrl(file);
      setTempImageFile(file);
      setTempImageUrl(previewUrl);
      setShowCropper(true);
      setIsProcessing(false);
    } catch (error) {
      logger.error('Failed to process image:', error);
      const errorMsg = 'Failed to process image. Please try again.';
      setUploadError(errorMsg);
      onError?.(errorMsg);
      setIsProcessing(false);
    }
  };

  /**
   * Execute upload with retry logic for transient failures
   */
  const executeUploadWithRetry = useCallback(async (
    file: File,
    cropArea: CropArea,
    uploadFn: (file: File, cropData?: CropArea) => Promise<void>
  ): Promise<void> => {
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await uploadFn(file, cropArea);
        return; // Success - exit retry loop
      } catch (error: unknown) {
        lastError = error;

        // Check if this is the last attempt or error is not retryable
        if (attempt >= maxRetries || !isRetryableUploadError(error)) {
          throw error;
        }

        // Calculate delay and wait before retry
        const delay = getUploadRetryDelay(
          attempt,
          UPLOAD_RETRY_CONFIG.baseDelayMs,
          UPLOAD_RETRY_CONFIG.maxDelayMs
        );

        logger.warn(
          `[useImageUpload] Upload attempt ${attempt + 1} failed, retrying in ${delay}ms:`,
          getErrorMessage(error)
        );

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    // Should not reach here, but throw last error just in case
    throw lastError;
  }, [maxRetries]);

  /**
   * Handle crop and upload with retry logic
   */
  const handleCropAndUpload = async (cropArea: CropArea) => {
    if (!tempImageFile || !onUpload) return;

    try {
      setShowCropper(false);
      setIsProcessing(true);
      setUploadError(null);

      // Optimize image with crop area (with timeout protection)
      let optimizedFile: File;
      try {
        optimizedFile = await optimizeImage(tempImageFile, {
          maxWidth,
          maxHeight,
          quality,
          format,
          cropArea
        });

        // Validate the optimized file is valid
        if (!optimizedFile || optimizedFile.size === 0) {
          throw new Error('Image processing produced an empty file');
        }
      } catch (optimizeError: unknown) {
        logger.error('[useImageUpload] Image optimization failed:', optimizeError);
        const errorMsg = getErrorMessage(optimizeError);
        setUploadError(errorMsg);
        onError?.(errorMsg);
        setIsProcessing(false);
        cleanupTempImage();
        return;
      }

      // Check if optimized file is still within limits (5MB for upload)
      if (!isFileSizeValid(optimizedFile, 5)) {
        // Further compress if needed
        try {
          optimizedFile = await optimizeImage(tempImageFile, {
            maxWidth: maxWidth * 0.75,
            maxHeight: maxHeight * 0.75,
            quality: quality * 0.8,
            format,
            cropArea
          });

          // Validate re-compressed file
          if (!optimizedFile || optimizedFile.size === 0) {
            throw new Error('Image re-compression produced an empty file');
          }
        } catch (recompressError: unknown) {
          logger.error('[useImageUpload] Image re-compression failed:', recompressError);
          const errorMsg = 'Failed to compress image. Please try a smaller image.';
          setUploadError(errorMsg);
          onError?.(errorMsg);
          setIsProcessing(false);
          cleanupTempImage();
          return;
        }

        if (!isFileSizeValid(optimizedFile, 5)) {
          const error = 'Image is still too large after compression. Please choose a smaller image.';
          setUploadError(error);
          onError?.(error);
          setIsProcessing(false);
          cleanupTempImage();
          return;
        }
      }

      // Call the upload handler with retry logic
      await executeUploadWithRetry(optimizedFile, cropArea, onUpload);

      // Clean up on success
      cleanupTempImage();
      setIsProcessing(false);

    } catch (error: unknown) {
      logger.error('Failed to upload image after retries:', error);

      // Provide user-friendly error messages for common cases
      let errorMsg = getErrorMessage(error);

      // Check for specific error patterns and provide better messages
      const lowerMsg = errorMsg.toLowerCase();
      if (lowerMsg.includes('timeout')) {
        errorMsg = 'Upload timed out. Please check your connection and try again.';
      } else if (lowerMsg.includes('network') || lowerMsg.includes('fetch')) {
        errorMsg = 'Network error. Please check your connection and try again.';
      } else if (lowerMsg.includes('cors') || lowerMsg.includes('cross-origin')) {
        errorMsg = 'Connection error. Please refresh the page and try again.';
      }

      setUploadError(errorMsg);
      onError?.(errorMsg);
      setIsProcessing(false);
      cleanupTempImage();
    }
  };

  /**
   * Clean up temporary image
   */
  const cleanupTempImage = () => {
    if (tempImageUrl) {
      revokePreviewUrl(tempImageUrl);
      setTempImageUrl(null);
    }
    setTempImageFile(null);
  };

  /**
   * Handle cropper cancel
   */
  const handleCropperCancel = () => {
    setShowCropper(false);
    cleanupTempImage();
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Reset upload state
   */
  const resetUpload = () => {
    setIsProcessing(false);
    setUploadError(null);
    setShowCropper(false);
    cleanupTempImage();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    // State
    isProcessing,
    uploadError,
    showCropper,
    tempImageUrl,
    
    // Methods
    handleImageSelect,
    handleCropAndUpload,
    handleCropperCancel,
    resetUpload,
    
    // Refs
    fileInputRef
  };
};